"use client";

import { useState, useEffect } from "react";
import { UploadCloud, FileDown, Loader2, CheckCircle } from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL!.replace(/\/$/, "");

type Status = "idle" | "uploading" | "running" | "done" | "error";

export default function VariantPage() {
  const [bam, setBam] = useState<File | null>(null);
  const [ref, setRef] = useState<File | null>(null);

  const [status, setStatus] = useState<Status>("idle");
  const [progress, setProgress] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);

  // ================= RUN =================
  const runPipeline = async () => {
    if (!bam || !ref) return;

    setStatus("uploading");
    setProgress(20);

    const fd = new FormData();
    fd.append("bam", bam);
    fd.append("ref", ref);

    const res = await fetch(`${API}/api/variant/run`, {
      method: "POST",
      body: fd,
    });

    if (!res.ok) {
      setStatus("error");
      return;
    }

    const data = await res.json();
    setJobId(data.job_id);
    setStatus("running");
    setProgress(40);
  };

  // ================= POLLING =================
  useEffect(() => {
    if (!jobId || status !== "running") return;

    const interval = setInterval(async () => {
      const res = await fetch(`${API}/api/variant/status/${jobId}`);
      const data = await res.json();

      if (data.status === "running") {
        setProgress((p) => Math.min(p + 5, 85));
      }

      if (data.status === "done") {
        setProgress(100);
        setStatus("done");
        clearInterval(interval);
      }

      if (data.status === "error") {
        setStatus("error");
        clearInterval(interval);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [jobId, status]);

  // ================= DOWNLOAD =================
  const download = () => {
    if (!jobId) return;
    window.location.href = `${API}/api/variant/result/${jobId}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-black via-purple-900 to-indigo-900 text-white">
      <div className="w-full max-w-xl bg-white/10 border border-white/20 rounded-2xl p-8 shadow-xl backdrop-blur">

        <h1 className="text-3xl font-bold text-center mb-6">
          Variant Calling
        </h1>

        <FileBox label="BAM file" file={bam} accept=".bam" onSelect={setBam} />
        <FileBox label="Reference FASTA" file={ref} accept=".fa,.fasta" onSelect={setRef} />

        <button
          onClick={runPipeline}
          disabled={!bam || !ref || status === "running"}
          className="mt-6 w-full py-3 rounded-xl font-semibold bg-gradient-to-r from-purple-600 to-indigo-600"
        >
          {status === "running" ? <Loader2 className="mx-auto animate-spin" /> : "Run Pipeline"}
        </button>

        {status !== "idle" && (
          <div className="mt-6">
            <div className="h-2 bg-white/20 rounded">
              <div className="h-full bg-purple-500" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-center mt-2 text-sm opacity-80">{status}</p>
          </div>
        )}

        {status === "done" && (
          <button
            onClick={download}
            className="mt-6 flex items-center justify-center gap-2 bg-green-600 py-3 rounded-xl w-full"
          >
            <FileDown /> Download VCF
          </button>
        )}
      </div>
    </div>
  );
}

// ================= FILE BOX =================
function FileBox({ label, file, accept, onSelect }: any) {
  return (
    <div className="border border-dashed border-white/40 rounded-xl p-5 text-center mt-4">
      <UploadCloud className="mx-auto mb-2 opacity-70" />
      <p>{label}</p>
      <input type="file" accept={accept} onChange={(e) => onSelect(e.target.files[0])} />
      {file && <p className="text-green-300 mt-2">{file.name}</p>}
    </div>
  );
}
